import { Injectable } from '@angular/core';
import { Map as MapTilerMap, Marker, LngLatLike } from '@maptiler/sdk';
import { MarkerManager } from '../Viewer/content.container/scene.container/maps-container/MarkerManager';

@Injectable({
  providedIn: 'root'
})
export class PlacemarkClusterService {
  private static readonly BACKGROUND_COLOR = 'rgba(147, 220, 237)';
  private static readonly BORDER_COLOR = 'gray';
  private static readonly BORDER_WIDTH = 1;
  private static readonly TEXT_COLOR = 'rgba(30, 30, 30, 1)';
  private static readonly FONT_STYLE = 'Segoe UI';
  private static readonly FONT_SIZE = 10;

  private clustering = false;
  public clusterMarkers = new Map<string, Marker>();
  public mapRef: MapTilerMap;
  public placemarkMarkers: Map<string, Marker>;
  public markerElementCache: Map<number, HTMLDivElement> = new Map();

  constructor() {}

  public init(mapRef: MapTilerMap, placemarkMarkers): void {
    this.mapRef = mapRef;
    this.placemarkMarkers = placemarkMarkers;
  }

  public setPlacemarkClustering(clustering: boolean): void {
    if (this.clustering !== clustering) {
      this.clustering = clustering;
      clustering ? this.createClusterMarkers() : this.clearClusterMarkers();
    }
  }

  public placemarkVisibilityUpdated(): void {
    if (this.clustering) {
      this.createClusterMarkers();
    }
  }

  public placemarkCreated(marker: Marker): void {
    if (MarkerManager.getAttr(marker, 'type') !== 'ADDRESS') {
      this.updateMarker(marker);
    }
  }

  public placemarkRemoved(marker: Marker): void {
    this.updateMarker(marker);
  }

  public placemarkUpdated(marker: Marker): void {
    this.updateMarker(marker);

    const originalElement = marker.getElement();
    const clonedElement = originalElement.cloneNode(true) as HTMLElement;
    const oldPositionedMarker = new Marker({
      element: clonedElement
    }).setLngLat(MarkerManager.getAttr(marker, "previousPosition"));

    this.updateMarker(oldPositionedMarker);
  }

  private clearClusterMarkers(): void {
    this.clusterMarkers.forEach(marker => marker.remove());
    this.clusterMarkers.clear();
  }

  private createClusterMarkers(): void {
    const processedPositions = new Set<string>();
    Array.from(this.placemarkMarkers.values()).forEach(marker => {
      if (MarkerManager.getAttr(marker, 'type') !== 'ADDRESS') {
        const key = this.createKeyFromPosition(marker);
        if (!processedPositions.has(key)) {
          this.updateMarker(marker);
          processedPositions.add(key);
        }
      }
    });
  }

  private updateMarker(marker: Marker): void {
    if (!this.clustering) {
      return;
    }
    const positionKey = this.createKeyFromPosition(marker);
    const samePositionPlacemarks = Array.from(this.placemarkMarkers.values()).filter(
      m => this.createKeyFromPosition(m) === positionKey && MarkerManager.getVisible(m) && MarkerManager.getAttr(m, 'type') !== 'ADDRESS'
    );

    samePositionPlacemarks.length > 1
      ? this.addOrUpdateMarker(positionKey, marker.getLngLat(), samePositionPlacemarks.length)
      : this.removeMarker(positionKey);
  }

  private addOrUpdateMarker(key: string, position: LngLatLike, count: number): void {
    if (this.clusterMarkers.has(key)) {
      this.updateNumberMarker(this.clusterMarkers.get(key), count);
    } else {
      const marker = this.createNumberMarker(position, count);
      this.clusterMarkers.set(key, marker);
    }
  }

  private removeMarker(key: string): void {
    const marker = this.clusterMarkers.get(key);
    if (marker) {
      marker.remove();
      this.clusterMarkers.delete(key);
    }
  }
  
  private createSvgContent(number: number): string {
    const [size, offsetX, offsetY, r] = [80, 28, 20, 9];
    const svgContent = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="transparent"/>
      <circle cx="${offsetX}" cy="${offsetY}" r="${r}" 
        fill="${PlacemarkClusterService.BACKGROUND_COLOR}" 
        stroke="${PlacemarkClusterService.BORDER_COLOR}" 
        stroke-width="${PlacemarkClusterService.BORDER_WIDTH}" />
      <text x="${offsetX}" y="${offsetY}" 
        text-anchor="middle" 
        dy=".3em" 
        font-family="${PlacemarkClusterService.FONT_STYLE}" 
        font-size="${PlacemarkClusterService.FONT_SIZE}" 
        fill="${PlacemarkClusterService.TEXT_COLOR}">${number}
      </text>
    </svg>
    `;
    return svgContent;
  }

  private createNumberMarker(position: LngLatLike, number: number): Marker {
    let el: HTMLDivElement;
    if (this.markerElementCache.has(number)) {
      const cachedElement = this.markerElementCache.get(number);
      el = cachedElement.cloneNode(true) as HTMLDivElement;
    } else {
      el = document.createElement('div');
      el.innerHTML = this.createSvgContent(number);
      el.style.cursor = 'default';
      el.style.pointerEvents = 'none';
      this.markerElementCache.set(number, el.cloneNode(true) as HTMLDivElement);
    }

    const marker: Marker = new Marker({ element: el });
    marker.setLngLat(position);
    marker.addTo(this.mapRef);
    
    MarkerManager.setZIndex(marker, 100010 + 1);
    return marker;
  }

  private updateNumberMarker(marker: Marker, number: number): void {
    // Update the SVG content directly in the DOM element
    const element = marker.getElement();
    element.innerHTML = this.createSvgContent(number);
  }

  public clearClusterPlacemarks(): void {
    this.clearClusterMarkers();
  }

  private createKeyFromPosition(marker: Marker): string {
    if (!marker.getLngLat()) return '';
    const lng  = marker.getLngLat().lng;
    const lat = marker.getLngLat().lat;
    const precision = 8;
    const multiplier = Math.pow(10, precision);
    const roundedLng = Math.round(lng * multiplier) / multiplier;
    const roundedLat = Math.round(lat * multiplier) / multiplier;
    return `${roundedLng.toFixed(precision)},${roundedLat.toFixed(precision)}`;
  }
}